import { Col, Row } from "antd";
import cuid from "cuid";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import {
  BANK_ACCOUNT_OWNER_TYPE,
  DEBIT_POSSIBLE_DATES,
  PAYMENT_FREQUENCIES,
} from "utils/constants";
import {
  bindInputProps,
  bindWebTextFieldInputProps,
  setDefaultIfEmpty,
} from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Select from "../Components/Select";
import validation from "./validation";

function View({
  initialValues,
  subscription,
  project,
  onNext,
  onBack,
  loading,
  showSepa,
}) {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("DETAIL", { returnObjects: true });

  const { handleSubmit, values, setFieldValue, ...formProps } = useFormik({
    initialValues: initialValues,
    onSubmit: onNext,
    enableReinitialize: true,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      { requireSepa: showSepa },
    ),
  });

  const PAYMENT_PAYER = {
    [true]: () => {
      if (subscription?.locked) return;
      const owner = {
        id: get(values, "payment.payer.owner.id", cuid()),
        firstName: "",
        lastName: "",
        isAdherent: false,
        type: BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
      };
      setFieldValue("payment.payer.owner", owner);
      if (get(values, "isPayerReceiver") === true) {
        setFieldValue("payment.receiver", {
          ...get(values, "payment.payer"),
          owner,
        });
      }
    },
    [false]: () => {
      if (subscription?.locked) return;
      const owner = {
        id: get(values, "payment.payer.owner.id", cuid()),
        firstName: get(project, "contact.user.firstname"),
        lastName: get(project, "contact.user.lastname"),
        isAdherent: true,
        type: BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
      };
      setFieldValue("payment.payer.owner", owner);
      if (get(values, "isPayerReceiver") === true) {
        setFieldValue("payment.receiver", {
          ...get(values, "payment.payer"),
          owner,
        });
      }
    },
  };

  useEffect(() => {
    if (get(initialValues, "payment.payer.owner.isAdherent")) {
      PAYMENT_PAYER[false]();
    }
  }, []);

  return (
    <Layout>
      <Header
        title="Renseignez vos informations bancaires"
        highlight="informations bancaires"
        subtitle="Vos informations bancaires sont protégées et nécessaires pour gérer vos cotisations"
        onBack={onBack}
      />
      <Row justify="center">
        <Col xs={24} md={18}>
          <div className="grid--1">
            <div className="grid--2">
              <Input
                placeholder={"ex : Doe"}
                label={"Nom titulaire du compte"}
                {...bindWebTextFieldInputProps({
                  name: "payment.payer.owner.lastName",
                  values,
                  setFieldValue,
                  ...formProps,
                })}
              />
              <Input
                placeholder={"ex : John"}
                label={"Prénom titulaire du compte"}
                {...bindWebTextFieldInputProps({
                  name: "payment.payer.owner.firstName",
                  values,
                  setFieldValue,
                  ...formProps,
                })}
              />
            </div>

            <div className="grid--2">
              <Input
                placeholder={"ex : FR76 3000 4001 2600 0100 6123 456"}
                label={"IBAN"}
                trim
                {...bindInputProps({
                  name: "payment.payer.IBAN",
                  values,
                  ...formProps,
                })}
              />
              <Input
                placeholder={"ex : PRNSFRP1"}
                label={"BIC"}
                trim
                {...bindInputProps({
                  name: "payment.payer.BIC",
                  values,
                  ...formProps,
                })}
              />
            </div>

            {showSepa && (
              <div className="grid--2">
                <Select
                  options={setDefaultIfEmpty(
                    get(subscription, "insurancePlan.fields.payment.frequency"),
                    Object.values(PAYMENT_FREQUENCIES),
                  ).map((value) => ({
                    value,
                    label: TRANSLATION.PAYMENT_FREQUENCY.OPTIONS[value],
                  }))}
                  label={TRANSLATION.PAYMENT_FREQUENCY.LABEL}
                  placeholder={"Sélectionnez ..."}
                  {...bindInputProps({
                    name: "payment.frequency",
                    values,
                    ...formProps,
                  })}
                  disabled={subscription?.locked}
                />
                <Select
                  options={setDefaultIfEmpty(
                    get(subscription, "insurancePlan.fields.payment.debitDay"),
                    DEBIT_POSSIBLE_DATES,
                  ).map((value) => ({
                    value,
                    label: value,
                  }))}
                  label={TRANSLATION.DEBIT_DAY.LABEL}
                  placeholder={"Sélectionnez ..."}
                  {...bindInputProps({
                    name: "payment.debitDay",
                    values,
                    ...formProps,
                  })}
                  disabled={true}
                />
              </div>
            )}
          </div>
        </Col>
      </Row>
      <ActionButtons
        disabled={!isEmpty(formProps.errors)}
        onNext={handleSubmit}
        onBack={onBack}
        loading={loading}
      />
    </Layout>
  );
}

export default View;
